import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ContributionModal({ open, onClose, moduleId, moduleTitle, onCreated, defaultCategory = "negotiation" }) {
  const [categories, setCategories] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(moduleId || "");
  const [category, setCategory] = useState(defaultCategory);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/contributions/categories").then((r) => setCategories(r.data)).catch(() => {});
    if (!moduleId) {
      api.get("/modules").then((r) => setModules(r.data)).catch(() => {});
    }
    setSelectedModule(moduleId || "");
    setCategory(defaultCategory);
  }, [open, moduleId, defaultCategory]);

  const submit = async () => {
    if (!selectedModule) {
      toast.error("Please choose a module");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contributions", { module_id: selectedModule, category, title, body });
      toast.success("Insight shared. Thank you.");
      onCreated?.(data);
      setTitle(""); setBody(""); setCategory(defaultCategory);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="contribution-modal" className="max-w-xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0A0A0A]">Share your experience</DialogTitle>
          <p className="text-sm text-[#525252]">
            {moduleId ? (
              <>Contributing to <strong className="text-[#0A0A0A]">{moduleTitle}</strong>. Firsthand stories with specific numbers help future readers most.</>
            ) : (
              <>Firsthand stories with specific numbers help future readers most. Pick the module your insight belongs to.</>
            )}
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {!moduleId && (
            <div>
              <label className="text-xs uppercase tracking-wider text-[#525252] font-medium block mb-1.5">Module</label>
              <Select value={selectedModule} onValueChange={setSelectedModule}>
                <SelectTrigger data-testid="contribution-module" className="w-full">
                  <SelectValue placeholder="Choose a module..." />
                </SelectTrigger>
                <SelectContent>
                  {modules.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.number} · {m.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-wider text-[#525252] font-medium block mb-1.5">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="contribution-category" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#525252] font-medium block mb-1.5">Headline</label>
            <input
              data-testid="contribution-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={140}
              placeholder="e.g. Bengaluru resale — always start 15% below ask"
              className="w-full px-3 py-2 border border-[#E5E5E0] focus:border-[#0A0A0A] focus:outline-none"
            />
            <div className="text-[10px] font-mono text-[#525252] mt-1">{title.length}/140</div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#525252] font-medium block mb-1.5">Your story</label>
            <textarea
              data-testid="contribution-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={6000}
              rows={8}
              placeholder="Specific numbers, timelines and outcomes. What surprised you? What would you do differently?"
              className="w-full px-3 py-2 border border-[#E5E5E0] focus:border-[#0A0A0A] focus:outline-none leading-relaxed resize-y"
            />
            <div className="text-[10px] font-mono text-[#525252] mt-1">{body.length}/6000 · minimum 40 characters</div>
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-[#E5E5E0] text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            data-testid="contribution-submit"
            onClick={submit}
            disabled={submitting || title.length < 6 || body.length < 40 || !selectedModule}
            className="px-4 py-2 bg-[#0A0A0A] text-white hover:bg-black disabled:opacity-50 transition-colors text-sm"
          >
            {submitting ? "Publishing..." : "Publish insight"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
